/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import { Modal, Input, Button, message, Upload } from 'antd';
import {
  InboxOutlined,
  SettingOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import {
  createContract,
  createVoucher,
  getContracts,
  getVouchers,
} from '~src/services/api/contracts';
import { useMutation, queryCache } from 'react-query';
import FileProgressContext from '~src/contexts/FileProgressContext';
import type { FileProgress } from '~src/contexts/FileProgressContext';
import { v4 as uuid } from 'uuid';
import IconButton from '~components/_common/IconButton/IconButton';

type ContractDto = {
  id: number;
  name: string;
  description?: string;
  reference?: string;
};

type VoucherDto = {
  id: number;
  name: string;
  description?: string;
  voucher_type: 1 | 2 | 3 | 4;
  tender_voucher_id?: number;
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const pad2 = (n: number) => String(n).padStart(2, '0');

const StepHeader: React.FC<{
  index: number;
  title: string;
}> = ({ index, title }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 8,
        marginTop: 14,
      }}>
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          background: '#1677ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          flex: '0 0 22px',
        }}>
        {index}
      </div>

      <div style={{ fontWeight: 700 }}>{title}</div>
    </div>
  );
};

export const AddOfferListModal: React.FC<{
  projectId?: number;
  tenderId?: string | number;
  visible?: boolean;
  onCancel?: () => void;

  // Needed for contract ref# prefill: "<NN>-<registration.reference_nr>"
  registrationReferenceNr?: string | number;
}> = ({
  projectId,
  tenderId,
  visible,
  onCancel,
  registrationReferenceNr,
  ...modalProps
}) => {
  const intl = useIntl();
  const InboxIcon = (InboxOutlined as unknown) as React.FC<any>;

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Inline details instead of Drawer
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [contract, setContract] = useState({
    name: '',
    description: '',
    reference: '',
  });

  const [voucher, setVoucher] = useState({
    name: '',
    description: '',
  });

  const [fileList, setFileList] = useState<any[]>([]);

  const { setFileProgressContext } = useContext(FileProgressContext);

  const submittingRef = useRef(false);

  // prevent re-init on refetch
  const wasVisibleRef = useRef(!!visible);

  const [createContractMut] = useMutation(createContract, {
    onSuccess: () => {
      // match the real query key used by useContractsUnified
      queryCache.invalidateQueries([
        'contracts',
        projectId,
        tenderId,
        undefined,
        undefined,
      ]);
      queryCache.invalidateQueries(['contracts', projectId, tenderId]);
    },
    onError: (err: any) => {
      console.error('createContract error:', err);
    },
  });

  const [createVoucherMut] = useMutation(createVoucher, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        'contracts',
        projectId,
        tenderId,
        undefined,
        undefined,
      ]);
      queryCache.invalidateQueries(['contracts', projectId, tenderId]);
    },
    onError: (err: any) => {
      console.error('createVoucher error:', err);
    },
  });

  // Wrap mutate() so we can await “completion” (even if it returns void)
  const mutateAsPromise = <TVariables,>(mutateFn: any, variables: TVariables) =>
    new Promise<void>((resolve, reject) => {
      mutateFn(variables, {
        onSuccess: () => resolve(),
        onError: (err: any) => reject(err),
      });
    });

  const resetAll = () => {
    setContract({ name: '', description: '', reference: '' });
    setVoucher({ name: '', description: '' });
    setFileList([]);
    setDetailsOpen(false);
  };

  const getErrText = (err: any) => {
    const serverMsg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data ||
      err?.message;
    return serverMsg ? String(serverMsg) : 'Unknown error';
  };

  const waitForCreatedContract = async ({
    projectIdNum,
    tenderIdNum,
    beforeMaxId,
    desiredReference,
    desiredName,
  }: {
    projectIdNum: number;
    tenderIdNum: number;
    beforeMaxId: number;
    desiredReference: string;
    desiredName: string;
  }): Promise<ContractDto> => {
    const startedAt = Date.now();
    const timeoutMs = 15000;

    while (Date.now() - startedAt < timeoutMs) {
      const list: ContractDto[] = await getContracts({
        projectId: projectIdNum,
        tenderId: tenderIdNum,
      });

      // Prefer exact match by reference (you set it)
      const byRef = list.find(
        (c) =>
          (c.reference ?? '').trim() === (desiredReference ?? '').trim() &&
          c.id > beforeMaxId
      );
      if (byRef) return byRef;

      // fallback: name match
      const byName = list.find(
        (c) =>
          (c.name ?? '').trim() === desiredName.trim() && c.id > beforeMaxId
      );
      if (byName) return byName;

      // fallback: newest id
      const newest = [...list].sort((a, b) => b.id - a.id)[0];
      if (newest && newest.id > beforeMaxId) return newest;

      await sleep(400);
    }

    throw new Error('Timed out waiting for created contract to appear');
  };

  const waitForCreatedVoucher = async ({
    projectIdNum,
    tenderIdNum,
    contractId,
    beforeMaxId,
    desiredName,
  }: {
    projectIdNum: number;
    tenderIdNum: number;
    contractId: number;
    beforeMaxId: number;
    desiredName: string;
  }): Promise<VoucherDto> => {
    const startedAt = Date.now();
    const timeoutMs = 15000;

    while (Date.now() - startedAt < timeoutMs) {
      const list: VoucherDto[] = await getVouchers({
        projectId: projectIdNum,
        tenderId: tenderIdNum,
        contractId,
      });

      // OfferList voucher_type === 2
      const exact = list.find(
        (v) =>
          v.voucher_type === 2 &&
          (v.name ?? '').trim() === desiredName.trim() &&
          v.id > beforeMaxId
      );
      if (exact) return exact;

      const newestOfferList = [...list]
        .filter((v) => v.voucher_type === 2)
        .sort((a, b) => b.id - a.id)[0];

      if (newestOfferList && newestOfferList.id > beforeMaxId)
        return newestOfferList;

      await sleep(400);
    }

    throw new Error('Timed out waiting for created voucher to appear');
  };

  const isDanish = useMemo(() => {
    const loc = (intl.locale || '').toLowerCase();
    return loc === 'da' || loc.startsWith('da-');
  }, [intl.locale]);

  const prefillDefaultsOnOpen = async () => {
    if (!projectId || !tenderId) return;

    const projectIdNum = Number(projectId);
    const tenderIdNum = Number(tenderId);

    // Titles
    const title = isDanish ? 'Tilbudsliste' : 'Offer list';

    // Reference: "<NN>-<registration.reference_nr>"
    let nextRef = '';
    try {
      const existing: ContractDto[] = await getContracts({
        projectId: projectIdNum,
        tenderId: tenderIdNum,
      });

      const numberPart = pad2((existing?.length || 0) + 1);
      const regRef =
        registrationReferenceNr != null ? String(registrationReferenceNr) : '';
      nextRef = regRef ? `${numberPart}-${regRef}` : `${numberPart}-`;
    } catch (e) {
      // If fetch fails, still set a reasonable fallback
      const regRef =
        registrationReferenceNr != null ? String(registrationReferenceNr) : '';
      nextRef = regRef ? `01-${regRef}` : `01-`;
    }

    // Prefill required strings (and keep descriptions "NULL" => empty)
    setContract({
      name: title,
      description: '',
      reference: nextRef,
    });

    setVoucher({
      name: title,
      description: '',
    });
  };

  // Only reset/prefill when modal OPENS (not on background refetches)
  useEffect(() => {
    const wasVisible = wasVisibleRef.current;

    if (visible && !wasVisible) {
      setDetailsOpen(false);
      setFileList([]);
      prefillDefaultsOnOpen();
    }

    wasVisibleRef.current = !!visible;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const canSubmit = useMemo(() => {
    if (!projectId || !tenderId) return false;
    if (!contract.name?.trim()) return false;
    if (!voucher.name?.trim()) return false;
    return true;
  }, [projectId, tenderId, contract.name, voucher.name]);

  const handleConfirm = async () => {
    if (!projectId || !tenderId) {
      message.error(intl.formatMessage({ id: 'missing-project-or-tender' }));
      return;
    }

    if (!canSubmit) return;
    if (isSubmitting || submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);

    const projectIdNum = Number(projectId);
    const tenderIdNum = Number(tenderId);

    const contractPayload = {
      name: contract.name,
      description: contract.description,
      reference: contract.reference,
    };

    const voucherPayload = {
      name: voucher.name,
      description: voucher.description,
    };

    try {
      // ---- BEFORE snapshots
      const beforeContracts: ContractDto[] = await getContracts({
        projectId: projectIdNum,
        tenderId: tenderIdNum,
      });
      const beforeMaxContractId =
        beforeContracts.length > 0
          ? Math.max(...beforeContracts.map((c) => c.id))
          : 0;

      // 1) Create contract (returns void)
      await mutateAsPromise(createContractMut, {
        projectId: projectIdNum,
        tenderId: tenderIdNum,
        data: contractPayload,
      });

      // 1b) WAIT + GET contract id
      const createdContract = await waitForCreatedContract({
        projectIdNum,
        tenderIdNum,
        beforeMaxId: beforeMaxContractId,
        desiredReference: contractPayload.reference,
        desiredName: contractPayload.name,
      });

      // ---- Voucher snapshot
      const beforeVouchers: VoucherDto[] = await getVouchers({
        projectId: projectIdNum,
        tenderId: tenderIdNum,
        contractId: createdContract.id,
      });
      const beforeMaxVoucherId =
        beforeVouchers.length > 0
          ? Math.max(...beforeVouchers.map((v) => v.id))
          : 0;

      // 2) Create voucher (OfferList)
      await mutateAsPromise(createVoucherMut, {
        projectId: projectIdNum,
        tenderId: tenderIdNum,
        contractId: createdContract.id,
        data: {
          name: voucherPayload.name,
          description: voucherPayload.description,
          voucher_type: 2,
          sort: 0,
          voucher_group_id: undefined,
        },
      });

      // 2b) WAIT + GET voucher (tender_voucher_id)
      const createdVoucher = await waitForCreatedVoucher({
        projectIdNum,
        tenderIdNum,
        contractId: createdContract.id,
        beforeMaxId: beforeMaxVoucherId,
        desiredName: voucherPayload.name,
      });

      // 3) Upload pipeline (OPTIONAL)
      const voucherUploadId =
        createdVoucher.tender_voucher_id ?? createdVoucher.id;

      if (fileList && fileList.length > 0) {
        setFileProgressContext((prev: any) => {
          const additions = fileList.map((f: any) => {
            const item = {
              id: uuid(),
              file: f?.originFileObj || f,
              shouldStartUpload: true,
              isDone: false,
              projectId: projectIdNum,
              tenderId: String(tenderIdNum),
              contractId: String(createdContract.id),
              voucherId: String(voucherUploadId),
              tenderVoucherId: voucherUploadId,
              entityCreated: false,
              commit: undefined as any,
              progress: 0,
            };

            // @ts-ignore
            return item as FileProgress;
          });

          return [...(prev || []), ...additions];
        });
      }

      message.success(intl.formatMessage({ id: 'offerlist-created-success' }));
      resetAll();
      onCancel?.();
    } catch (err: any) {
      console.error('AddOfferListModal error:', err);
      message.error(
        `${intl.formatMessage({ id: 'offerlist-create-failed' })}: ${getErrText(
          err
        )}`
      );
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <IconButton
            color={'black'}
            backgroundOff={true}
            icon={['far', 'briefcase']}
            customStyle={{ fontSize: '20px' }}
          />
          {intl.formatMessage({ id: 'add-offer-list' })}
        </div>
      }
      visible={visible}
      width={520}
      centered
      onCancel={() => {
        if (isSubmitting) return;
        resetAll();
        onCancel?.();
      }}
      footer={
        <div
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Button
            type="primary"
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
            onClick={handleConfirm}>
            {intl.formatMessage({ id: 'create-offer-list' })}
          </Button>
        </div>
      }
      destroyOnClose
      {...modalProps}>
      {/* Primary: upload */}
      <div>
        <Upload.Dragger
          multiple={false}
          maxCount={1}
          fileList={fileList as any}
          showUploadList={{ showRemoveIcon: !isSubmitting }}
          onChange={({ fileList: newList }) => {
            if (isSubmitting) return;
            setFileList(newList);
          }}
          disabled={isSubmitting}>
          <p className="ant-upload-drag-icon">
            <InboxIcon />
          </p>
          <p className="ant-upload-text">
            {intl.formatMessage({ id: 'click-or-drag-file-here' })}
          </p>
          <p className="ant-upload-hint">
            {intl.formatMessage({ id: 'support-single-upload' })}
          </p>
          <p className="ant-upload-hint">
            {intl.formatMessage({ id: 'offerlist-file-optional-hint' })}
          </p>
        </Upload.Dragger>

        <div style={{ marginTop: 8, fontSize: 12, color: '#7a829b' }}>
          {fileList?.length
            ? intl.formatMessage(
                { id: 'offerlist-selected-file' },
                {
                  name:
                    fileList?.[0]?.name ||
                    fileList?.[0]?.originFileObj?.name ||
                    '',
                }
              )
            : intl.formatMessage({ id: 'offerlist-no-file-selected' })}
        </div>
      </div>

      {/* Make Edit Details look like a real button */}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
        <Button
          type="default"
          icon={<SettingOutlined rev={''} />}
          disabled={isSubmitting}
          onClick={() => setDetailsOpen((v) => !v)}
          style={{
            borderRadius: 8,
            paddingLeft: 14,
            paddingRight: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
          {intl.formatMessage({ id: 'offerlist-open-details' })}
          <span style={{ marginLeft: 6 }}>
            {detailsOpen ? <UpOutlined rev={''} /> : <DownOutlined rev={''} />}
          </span>
        </Button>
      </div>

      {/* Inline details (expand UNDER the button) */}
      {detailsOpen && (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              marginBottom: 10,
              fontSize: 12,
              color: '#7a829b',
              textAlign: 'center',
            }}>
            {intl.formatMessage({ id: 'offerlist-details-drawer-hint' })}
          </div>

          <StepHeader
            index={1}
            title={intl.formatMessage({ id: 'contract' })}
          />

          <Input
            placeholder={intl.formatMessage({ id: 'name' })}
            value={contract.name}
            onChange={(e) => setContract({ ...contract, name: e.target.value })}
            style={{ marginBottom: 10 }}
            disabled={isSubmitting}
          />

          <Input
            placeholder={intl.formatMessage({ id: 'reference' })}
            value={contract.reference}
            onChange={(e) =>
              setContract({ ...contract, reference: e.target.value })
            }
            style={{ marginBottom: 10 }}
            disabled={isSubmitting}
          />

          <Input
            placeholder={intl.formatMessage({ id: 'description' })}
            value={contract.description}
            onChange={(e) =>
              setContract({ ...contract, description: e.target.value })
            }
            style={{ marginBottom: 6 }}
            disabled={isSubmitting}
          />

          <StepHeader
            index={2}
            title={(() => {
              const t = intl.formatMessage({ id: 'voucher' });
              return t.charAt(0).toUpperCase() + t.slice(1);
            })()}
          />

          <Input
            placeholder={intl.formatMessage({ id: 'name' })}
            value={voucher.name}
            onChange={(e) => setVoucher({ ...voucher, name: e.target.value })}
            style={{ marginBottom: 10 }}
            disabled={isSubmitting}
          />

          <Input
            placeholder={intl.formatMessage({ id: 'description' })}
            value={voucher.description}
            onChange={(e) =>
              setVoucher({ ...voucher, description: e.target.value })
            }
            disabled={isSubmitting}
          />
        </div>
      )}
    </Modal>
  );
};

export default AddOfferListModal;
